import styled, { keyframes, css } from 'styled-components'
import {
  COLORS,
  SPACING,
  BORDER_RADIUS,
  TRANSITIONS,
  SHADOWS,
  MEDIA,
  TYPOGRAPHY,
  FONT_WEIGHT,
} from '../../../../../styles/theme'

// Animations
const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

// Main container - three panel layout
export const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING.xl};
  height: 650px;

  ${MEDIA.mobile} {
    flex-direction: column;
    height: auto;
    max-height: 80dvh;
    gap: ${SPACING.md};
  }
`

// ===== LEFT PANEL: ROSTER =====
export const RosterPanel = styled.div`
  flex: 0 0 340px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  ${MEDIA.mobile} {
    flex: 1;
    min-height: 300px;
    max-height: 400px;
  }
`

export const RosterHeader = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: ${SPACING.md};
  border-bottom: 1px solid var(--color-border);
  margin-bottom: ${SPACING.md};
  flex-shrink: 0;
`

export const RosterScrollArea = styled.div`
  overflow-y: auto;
  padding-right: ${SPACING.xs};
  flex: 1;
  min-height: 0;
`

export const RosterSection = styled.div`
  margin-bottom: ${SPACING.lg};

  &:last-child {
    margin-bottom: 0;
  }
`

export const RosterSectionHeader = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${SPACING.sm};
`

export const RosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 150px);
  gap: ${SPACING.md};

  ${MEDIA.mobile} {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: ${SPACING.sm};
  }
`

// Character card in roster
export const CharacterCard = styled.div<{
  $isInTeam: boolean
  $isTeamFull: boolean
  $isSelected: boolean
}>`
  display: flex;
  flex-direction: column;
  background: var(--color-bg-card);
  border: 2px solid
    ${(props) =>
      props.$isSelected ? COLORS.primary : props.$isInTeam ? 'var(--color-border)' : 'transparent'};
  border-radius: ${BORDER_RADIUS.lg};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  position: relative;
  opacity: ${(props) => (props.$isInTeam ? 0.5 : 1)};

  &:hover {
    border-color: ${(props) => (props.$isSelected ? COLORS.primary : `${COLORS.primary}80`)};
    box-shadow: ${SHADOWS.md};
  }
`

export const CardPortraitContainer = styled.div<{ $color: string }>`
  width: 100%;
  aspect-ratio: 1;
  background: ${(props) => props.$color};
  position: relative;
  overflow: hidden;
  border-radius: ${BORDER_RADIUS.lg} ${BORDER_RADIUS.lg} 0 0;
  flex-shrink: 0;
`

export const CardPortraitInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const CardPortraitImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const CardPortraitInitials = styled.div`
  font-size: ${TYPOGRAPHY.h2.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  ${MEDIA.mobile} {
    font-size: ${TYPOGRAPHY.h3.fontSize};
  }
`

export const CardInTeamOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

// Hover actions overlay
export const CardActionsOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: ${SPACING.xs};
  padding: ${SPACING.xs};
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  opacity: 0;
  transition: ${TRANSITIONS.fast};

  ${CharacterCard}:hover & {
    opacity: 1;
  }
`

export const CardActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 8px;
  border: none;
  border-radius: ${BORDER_RADIUS.sm};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};

  ${(props) =>
    props.$variant === 'primary'
      ? css`
          background: ${COLORS.primary};
          color: white;
          &:hover {
            background: ${COLORS.primaryHover};
          }
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `
      : css`
          background: rgba(255, 255, 255, 0.9);
          color: #1f2937;
          &:hover {
            background: white;
          }
        `}
`

export const CardContent = styled.div`
  padding: ${SPACING.sm};
`

export const CardName = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const CardRole = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
`

// Create New card (special card at end of roster)
export const CreateNewCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 2px dashed var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.lg};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  min-height: 180px;
  gap: ${SPACING.sm};
  color: var(--color-text-tertiary);

  &:hover {
    border-color: ${COLORS.primary};
    border-style: solid;
    color: ${COLORS.primary};
    background: ${COLORS.primary}08;
  }

  ${MEDIA.mobile} {
    min-height: 140px;
    gap: ${SPACING.xs};
  }
`

export const CreateNewIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${BORDER_RADIUS.full};
  background: var(--color-bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${TRANSITIONS.fast};

  ${CreateNewCard}:hover & {
    background: ${COLORS.primary}15;
  }
`

export const CreateNewText = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
`

// ===== CENTER PANEL: TEAM SLOTS =====
export const TeamPanel = styled.div`
  flex: 0 0 160px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${MEDIA.mobile} {
    flex: 0 0 auto;
    width: 100%;
    border-top: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    padding: ${SPACING.sm} 0;
  }
`

export const TeamHeader = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: ${SPACING.md};
  border-bottom: 1px solid var(--color-border);
  margin-bottom: ${SPACING.md};
  width: 100%;
  text-align: center;

  ${MEDIA.mobile} {
    border-bottom: none;
    padding-bottom: ${SPACING.sm};
    margin-bottom: ${SPACING.sm};
  }
`

export const SlotsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
  flex: 1;

  ${MEDIA.mobile} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${SPACING.xs};
  }
`

export const TeamSlot = styled.div<{ $isEmpty: boolean }>`
  width: 120px;
  height: 80px;
  border: 2px ${(props) => (props.$isEmpty ? 'dashed' : 'solid')}
    ${(props) => (props.$isEmpty ? 'var(--color-border-dark)' : 'var(--color-border)')};
  border-radius: ${BORDER_RADIUS.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$isEmpty ? 'transparent' : 'var(--color-bg-card)')};
  transition: ${TRANSITIONS.fast};
  position: relative;
  overflow: hidden;
  gap: ${SPACING.xs};

  ${MEDIA.mobile} {
    width: 52px;
    height: 52px;
    border-radius: ${BORDER_RADIUS.md};
  }
`

export const SlotEmptyText = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-muted);

  ${MEDIA.mobile} {
    display: none;
  }
`

export const SlotPortrait = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${BORDER_RADIUS.full};
  background: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  ${MEDIA.mobile} {
    width: 32px;
    height: 32px;
  }
`

export const SlotPortraitImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const SlotInitials = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: white;

  ${MEDIA.mobile} {
    font-size: ${TYPOGRAPHY.caption.fontSize};
  }
`

export const SlotName = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;

  ${MEDIA.mobile} {
    display: none;
  }
`

// ===== RIGHT PANEL: PROFILE =====
export const ProfilePanel = styled.div`
  flex: 1;
  min-width: 280px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--color-border);
  padding-left: ${SPACING.xl};
  overflow: hidden;

  ${MEDIA.mobile} {
    border-left: none;
    border-top: 1px solid var(--color-border);
    padding-left: 0;
    padding-top: ${SPACING.md};
    min-width: 0;
    min-height: 200px;
    max-height: 300px;
  }
`

export const ProfileHeader = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: ${SPACING.md};
  border-bottom: 1px solid var(--color-border);
  margin-bottom: ${SPACING.md};
`

export const ProfileContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: ${SPACING.md};
  animation: ${slideInRight} 0.2s ease;
`

export const ProfileEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: ${SPACING.md};
  color: var(--color-text-tertiary);
  text-align: center;
  padding: ${SPACING.xl};
`

export const ProfileEmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${BORDER_RADIUS.full};
  background: var(--color-bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
`

export const ProfileEmptyText = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

// Profile View (when character selected)
export const ProfilePortraitLarge = styled.div<{ $color: string }>`
  width: 160px;
  height: 160px;
  border-radius: ${BORDER_RADIUS.xl};
  background: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0 auto ${SPACING.lg};
  position: relative;

  ${MEDIA.mobile} {
    width: 100px;
    height: 100px;
    margin-bottom: ${SPACING.md};
  }
`

export const ProfilePortraitImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const ProfilePortraitInitials = styled.div`
  font-size: ${TYPOGRAPHY.h1.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  ${MEDIA.mobile} {
    font-size: ${TYPOGRAPHY.h2.fontSize};
  }
`

export const ProfileName = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  text-align: center;
`

export const ProfileRole = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-align: center;
  margin-top: 4px;
`

export const ProfileRoleSummary = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-primary);
  text-align: left;
  margin-top: ${SPACING.md};
  line-height: 1.5;
`

export const ProfileSection = styled.div`
  margin-top: ${SPACING.lg};
`

export const ProfileSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${SPACING.sm};

  svg {
    flex-shrink: 0;
    opacity: 0.7;
  }
`

export const ProfileSectionList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

export const ProfileSectionItem = styled.li`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-primary);
  padding: ${SPACING.xs} 0;
  padding-left: ${SPACING.md};
  position: relative;
  line-height: 1.4;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    width: 4px;
    height: 4px;
    border-radius: ${BORDER_RADIUS.full};
    background: var(--color-text-tertiary);
  }
`

// Sticky action bar for profile panel
export const ProfileActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} 0;
  margin-bottom: ${SPACING.md};
  position: sticky;
  top: 0;
  background: var(--color-bg-primary);
  z-index: 1;

  ${MEDIA.mobile} {
    flex-wrap: wrap;
    justify-content: center;
    gap: ${SPACING.xs};
  }
`

export const ProfileActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${SPACING.sm} ${SPACING.lg};
  border-radius: ${BORDER_RADIUS.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};

  ${(props) =>
    props.$variant === 'primary'
      ? css`
          background: ${COLORS.primary};
          border: none;
          color: white;

          &:hover {
            background: ${COLORS.primaryHover};
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `
      : css`
          background: transparent;
          border: 1px solid var(--color-border-dark);
          color: var(--color-text-secondary);

          &:hover:not(:disabled) {
            border-color: ${COLORS.primary};
            color: ${COLORS.primary};
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}

  ${MEDIA.mobile} {
    padding: ${SPACING.xs} ${SPACING.sm};
    font-size: ${TYPOGRAPHY.caption.fontSize};
  }
`

// ===== CREATE FORM (Right Panel State) =====
export const CreateFormContainer = styled.div`
  animation: ${slideInRight} 0.2s ease;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

export const CreateFormHeader = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin-bottom: ${SPACING.lg};
  flex-shrink: 0;
`

export const CreateFormContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: ${SPACING.md};
`

export const CreateFormField = styled.div`
  margin-bottom: ${SPACING.md};
`

export const CreateFormLabel = styled.label`
  display: block;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${SPACING.xs};
`

export const CreateFormInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 ${SPACING.sm};
  background: var(--color-bg-card);
  border: 1px solid var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.md};
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  outline: none;
  transition: ${TRANSITIONS.fast};

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    border-color: ${COLORS.primary};
  }
`

export const CreateFormTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${SPACING.sm};
  background: var(--color-bg-card);
  border: 1px solid var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.md};
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  outline: none;
  transition: ${TRANSITIONS.fast};
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    border-color: ${COLORS.primary};
  }
`

export const CreateFormArrayField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const CreateFormArrayItem = styled.div`
  display: flex;
  gap: ${SPACING.xs};
  align-items: center;
`

export const CreateFormArrayInput = styled.input`
  flex: 1;
  height: 32px;
  padding: 0 ${SPACING.sm};
  background: var(--color-bg-card);
  border: 1px solid var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.md};
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  outline: none;
  transition: ${TRANSITIONS.fast};

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    border-color: ${COLORS.primary};
  }
`

export const CreateFormArrayRemove = styled.button`
  width: 24px;
  height: 24px;
  border-radius: ${BORDER_RADIUS.full};
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${TRANSITIONS.fast};

  &:hover {
    color: #ef4444;
    background: #ef444415;
  }
`

export const CreateFormArrayAdd = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${SPACING.xs} ${SPACING.sm};
  background: transparent;
  border: 1px dashed var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.md};
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  margin-top: ${SPACING.xs};

  &:hover {
    border-color: ${COLORS.primary};
    color: ${COLORS.primary};
  }
`

export const CreateFormFooter = styled.div`
  display: flex;
  gap: ${SPACING.sm};
  justify-content: flex-end;
  padding-top: ${SPACING.md};
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
  margin-top: auto;

  ${MEDIA.mobile} {
    flex-wrap: wrap;
    justify-content: center;
  }
`

// Loading state
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
`

// Locked slot (tier-gated)
export const LockedSlotContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;

  &:hover {
    color: rgba(102, 126, 234, 0.8);
  }
`

export const LockedSlotText = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`
